import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable, of, shareReplay, switchMap, tap } from "rxjs";

import { JwtService } from "../../auth/services/jwt/jwt.service";
import { Result, ResultWithValue } from "../../apiResults/result";
import { ChangeEmailDto } from "../dtos/ChangeEmailDto";
import { CheckUserDto, UserAccountDto, UserProfileSettings } from "../dtos/UserAccountDto";
import { ChangePasswordDto } from "../dtos/ChangePasswordDto";
import { ChangeProfileResultDto } from "../dtos/changeProfileResultDto";
import { AvatarBadgeDto } from "../../components/avatar-badge/dtos/avatarBadgeDto";
import { UpdateUserAccountDto } from "../dtos/UpdateUserAccountDto";
import { ServiceResponse } from "../../service-response/serviceResponse";


export const USER_PROFILE_API = {
    Get: () => 'api/userprofile/',
    Post: () => 'api/userprofile/',
    ChangeEmail: () => 'api/userprofile/email/',
    ChangePassword: () => 'api/userprofile/password/',
    ChangeColorScheme: () => 'api/userprofile/colorscheme/',
    SaveProfilePicture: () => 'api/userprofile/profilepicture/',
    DeleteProfilePicture: () => `api/userprofile/profilepicture/`,
    ChangeCopyMeOnEmailsSetting: (copyMeOnEmails:boolean) => `api/userprofile/settings?copymeonemails=${copyMeOnEmails}`,
    GetSettings: ()  => `api/userprofile/settings`,
    IsUserValid: () => `api/userprofile/isValid`,
    SendSSOCodeValidation: () => `api/userprofile/sendSSOCode`, 
}

@Injectable({
    providedIn: 'root',
})
export class UserProfileService {
    constructor(private http: HttpClient, private jwtService: JwtService) {

    }

    userProfileChanged$ = new BehaviorSubject(true);

    signedInUserInformation$: Observable<ResultWithValue<UserAccountDto>> = this.userProfileChanged$.pipe(
        switchMap(() => this.http.get<ResultWithValue<UserAccountDto>>(USER_PROFILE_API.Get())),
        catchError((err: HttpErrorResponse) => {
            const fail: ResultWithValue<UserAccountDto> = {
                isFailure: true,
                isSuccess: false,
                status: err.status,
                value: {} as UserAccountDto
            }
            return of(fail);
        }
        ),
        shareReplay()
    );



    updateUserInformation(userAccount: UpdateUserAccountDto) {
        return this.http.post<Result>(USER_PROFILE_API.Post(), userAccount).pipe(
            tap(() => { this.userProfileChanged$.next(true) }),
            catchError(err => {
                return of<Result>(err.error);
            })
        );
    }

    changeEmail(userAccount: ChangeEmailDto) {
        return this.http.post<ResultWithValue<ChangeProfileResultDto>>(USER_PROFILE_API.ChangeEmail(), userAccount).pipe(
            tap(
                result => {
                    this.handleChangeProfileResponse(result)
                    this.userProfileChanged$.next(true);
                }
            ),
            catchError(err => {
                return of<Result>(err.error);
            })
        );
    }

    changePassword(changePassDto: ChangePasswordDto): Observable<Result | ResultWithValue<ChangeProfileResultDto>> {
        return this.http.post<ResultWithValue<ChangeProfileResultDto>>(USER_PROFILE_API.ChangePassword(), changePassDto).pipe(
            tap(
                result => this.handleChangeProfileResponse(result)
            ),
            catchError(err => {
                return of<Result>(err.error);
            })
        );
    }

    refreshUserProfile() {
        this.userProfileChanged$.next(true);
    }

    personalBadgeInfo(): AvatarBadgeDto {
        const token = this.jwtService.getToken();
        if (token?.accessToken) {
            const accessToken = this.jwtService.decodeToken(token.accessToken);
            return { initials: accessToken["given_name"], name: accessToken["name"], identifier: accessToken["email"], isInvited: true };
        }

        return { initials: "", name: '', identifier: "", isInvited: true };
    }

    getUserId(): string | null {
        const token = this.jwtService.getToken();
        if (token?.accessToken) {
            const accessToken = this.jwtService.decodeToken(token.accessToken);
            return accessToken["sub"];
        }

        return null;
    }

    getEmail(): string | null{
        const token = this.jwtService.getToken();
        if (token?.accessToken) {
            const accessToken = this.jwtService.decodeToken(token.accessToken);
            return accessToken["email"];
        }
        return null;
    }

    deleteProfilePicture() {
        return this.http.delete<ResultWithValue<boolean>>(USER_PROFILE_API.DeleteProfilePicture());
    }


    private handleChangeProfileResponse(result: ResultWithValue<ChangeProfileResultDto>) {
        if (result.isSuccess) {
            if (!result.value.tokenPair)
                return;

            this.jwtService.saveToken(result.value.tokenPair);
        }
    }


    public isInternalUser(): boolean {
        const token = this.jwtService.getToken();
        if (token?.accessToken) {
            const accessToken = this.jwtService.decodeToken(token.accessToken);
            const email: string = accessToken["email"];
            if (email && (email.toLowerCase().endsWith("@equusoft.com") || email.toLowerCase().endsWith("@workia.com"))) {
                return true;
            }
        }
        return false;
    }

    changeCopyMeOnEmailsSetting(copyMeOnEmails: boolean) {
        return this.http.post<Result>(USER_PROFILE_API.ChangeCopyMeOnEmailsSetting(copyMeOnEmails), {}).pipe(
            tap(() => { this.userProfileChanged$.next(true) }),
            catchError(err => {
                return of<Result>(err.error);
            })
        );
    }

    getUserProfileSettings(){
        const call$ = this.userProfileChanged$.pipe(
            switchMap(() => {
              return this.http.get<ResultWithValue<UserProfileSettings>>(USER_PROFILE_API.GetSettings());
            }),
            shareReplay()
          )
          return new ServiceResponse(call$);
    }

    checkIsUserValid(checkUserDto: CheckUserDto ){
        return this.http.post<Result>(USER_PROFILE_API.IsUserValid(), checkUserDto).pipe(
            catchError(err => {
                return of<Result>(err.error);
            })
        );
    }

    sendSSOCodeValidation(){
        return this.http.post<Result>(USER_PROFILE_API.SendSSOCodeValidation(), {}).pipe(
            catchError(err => {
                return of<Result>(err.error);
            })
        );
    }
}
